/**
 * Mythium Alpha Analyzer
 *
 * Crops an artwork image to its tight content bounding box and detects:
 * - hasAlpha: PNG with real transparency
 * - alphaCenterX: horizontal centroid 0..1
 * - alphaBottomFraction: where solid alpha ends 0..1
 * - footLeftFraction, footRightFraction: foot contact zone 0..1
 *
 * Port of artifacts/mythium-configurator/src/hooks/use-artwork-crop.ts
 * Pure browser API code. No frameworks.
 */

(function () {
  'use strict';

  const SCAN_MAX_DIM = 500;
  const ALPHA_THRESHOLD = 10;
  const COLOR_THRESHOLD = 35;
  const CORNER_SAMPLE = 8;
  const CORNER_CONSISTENCY_TOLERANCE = 25;
  const MIN_CONTENT_FRACTION = 0.04;
  const SOLID_ALPHA = 128;
  const FOOT_ZONE_FRACTION = 0.20;

  function cropArtworkForPreview(imageUrl, mimeType) {
    return new Promise((resolve) => {
      const fallback = (img) =>
        resolve({
          croppedUrl: imageUrl,
          aspectRatio: img && img.width ? img.width / img.height : 1,
          wasCropped: false,
          hasAlpha: false,
          alphaCenterX: 0.5,
          alphaBottomFraction: 1.0,
          footLeftFraction: 0,
          footRightFraction: 1,
        });

      const img = new Image();
      img.onerror = () => fallback(img);

      img.onload = () => {
        const origW = img.width;
        const origH = img.height;
        const scale = Math.min(1, SCAN_MAX_DIM / Math.max(origW, origH));
        const sw = Math.max(1, Math.round(origW * scale));
        const sh = Math.max(1, Math.round(origH * scale));

        const scanCanvas = document.createElement('canvas');
        scanCanvas.width = sw;
        scanCanvas.height = sh;
        const scanCtx = scanCanvas.getContext('2d', { willReadFrequently: true });
        if (!scanCtx) return fallback(img);
        scanCtx.drawImage(img, 0, 0, sw, sh);

        let data;
        try {
          data = scanCtx.getImageData(0, 0, sw, sh).data;
        } catch (e) {
          return fallback(img);
        }

        const isPng = mimeType === 'image/png';
        let bgR = 255, bgG = 255, bgB = 255;

        if (!isPng) {
          const cs = Math.min(CORNER_SAMPLE, Math.floor(sw / 8), Math.floor(sh / 8));
          if (cs < 1) return fallback(img);
          const cornerAvg = (cx, cy) => {
            let r = 0, g = 0, b = 0, n = 0;
            for (let dy = 0; dy < cs; dy++) {
              for (let dx = 0; dx < cs; dx++) {
                const i = ((cy + dy) * sw + (cx + dx)) * 4;
                r += data[i]; g += data[i + 1]; b += data[i + 2]; n++;
              }
            }
            return [r / n, g / n, b / n];
          };
          const corners = [
            cornerAvg(0, 0),
            cornerAvg(sw - cs, 0),
            cornerAvg(0, sh - cs),
            cornerAvg(sw - cs, sh - cs),
          ];
          const [r0, g0, b0] = corners[0];
          const consistent = corners.every(([r, g, b]) =>
            Math.abs(r - r0) < CORNER_CONSISTENCY_TOLERANCE &&
            Math.abs(g - g0) < CORNER_CONSISTENCY_TOLERANCE &&
            Math.abs(b - b0) < CORNER_CONSISTENCY_TOLERANCE
          );
          if (!consistent) return fallback(img);
          bgR = corners.reduce((s, c) => s + c[0], 0) / 4;
          bgG = corners.reduce((s, c) => s + c[1], 0) / 4;
          bgB = corners.reduce((s, c) => s + c[2], 0) / 4;
        }

        let minX = sw, minY = sh, maxX = 0, maxY = 0;
        let foundContent = false;
        let anyTransparentPixel = false;
        let alphaWeightSum = 0;
        let alphaTotalWeight = 0;

        for (let y = 0; y < sh; y++) {
          for (let x = 0; x < sw; x++) {
            const i = (y * sw + x) * 4;
            let isContent, weight;
            if (isPng) {
              const alpha = data[i + 3];
              if (alpha < 255) anyTransparentPixel = true;
              isContent = alpha > ALPHA_THRESHOLD;
              weight = alpha;
            } else {
              const dr = data[i] - bgR;
              const dg = data[i + 1] - bgG;
              const db = data[i + 2] - bgB;
              isContent = Math.sqrt(dr * dr + dg * dg + db * db) > COLOR_THRESHOLD;
              weight = 1;
            }
            if (isContent) {
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
              foundContent = true;
              alphaWeightSum += x * weight;
              alphaTotalWeight += weight;
            }
          }
        }

        const hasAlpha = isPng && anyTransparentPixel;

        const centXScanned = alphaTotalWeight > 0
          ? alphaWeightSum / alphaTotalWeight
          : (minX + maxX) / 2;
        const alphaCenterX = Math.max(0, Math.min(1,
          (centXScanned - minX) / Math.max(1, maxX - minX + 1)
        ));

        let solidMaxY = minY;
        if (isPng && foundContent) {
          for (let y = maxY; y >= minY; y--) {
            let rowHasSolid = false;
            for (let x = minX; x <= maxX; x++) {
              const i = (y * sw + x) * 4;
              if (data[i + 3] > SOLID_ALPHA) { rowHasSolid = true; break; }
            }
            if (rowHasSolid) { solidMaxY = y; break; }
          }
        }
        const alphaBottomFraction = (maxY > minY)
          ? Math.max(0.1, Math.min(1.0, (solidMaxY - minY + 1) / (maxY - minY + 1)))
          : 1.0;

        let footMinX = maxX;
        let footMaxX = minX;
        if (isPng && foundContent) {
          const contentH = maxY - minY;
          const footZoneTop = Math.max(minY, Math.round(solidMaxY - contentH * FOOT_ZONE_FRACTION));
          for (let y = footZoneTop; y <= solidMaxY; y++) {
            for (let x = minX; x <= maxX; x++) {
              const i = (y * sw + x) * 4;
              if (data[i + 3] > SOLID_ALPHA) {
                if (x < footMinX) footMinX = x;
                if (x > footMaxX) footMaxX = x;
              }
            }
          }
        }
        if (footMinX > footMaxX) { footMinX = minX; footMaxX = maxX; }
        const cropSpan = maxX - minX;
        const footLeftFraction = cropSpan > 0
          ? Math.max(0, Math.min(1, (footMinX - minX) / cropSpan))
          : 0;
        const footRightFraction = cropSpan > 0
          ? Math.max(0, Math.min(1, (footMaxX - minX) / cropSpan))
          : 1;

        if (!foundContent) return fallback(img);

        const cw = maxX - minX + 1;
        const ch = maxY - minY + 1;
        if (cw < sw * MIN_CONTENT_FRACTION || ch < sh * MIN_CONTENT_FRACTION) {
          return fallback(img);
        }

        const origMinX = Math.round(minX / scale);
        const origMinY = Math.round(minY / scale);
        const origMaxX = Math.round(maxX / scale);
        const origMaxY = Math.round(maxY / scale);
        const cropX = origMinX;
        const cropY = origMinY;
        const cropW = origMaxX - origMinX + 1;
        const cropH = origMaxY - origMinY + 1;

        const outCanvas = document.createElement('canvas');
        outCanvas.width = cropW;
        outCanvas.height = cropH;
        const outCtx = outCanvas.getContext('2d');
        if (!outCtx) return fallback(img);

        if (!isPng) {
          outCtx.fillStyle = `rgb(${Math.round(bgR)},${Math.round(bgG)},${Math.round(bgB)})`;
          outCtx.fillRect(0, 0, cropW, cropH);
        }
        outCtx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

        outCanvas.toBlob(
          (blob) => {
            if (!blob) return fallback(img);
            resolve({
              croppedUrl: URL.createObjectURL(blob),
              aspectRatio: cropW / cropH,
              wasCropped: true,
              hasAlpha: hasAlpha,
              alphaCenterX: alphaCenterX,
              alphaBottomFraction: alphaBottomFraction,
              footLeftFraction: footLeftFraction,
              footRightFraction: footRightFraction,
            });
          },
          isPng ? 'image/png' : 'image/jpeg',
          0.92
        );
      };

      img.src = imageUrl;
    });
  }

  window.MythiumAlphaAnalyzer = {
    cropArtworkForPreview: cropArtworkForPreview,
  };
})();
